import { useLocation } from '@reach/router';
import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

/**
 * OAuth redirect target for the TV QR device sign-in flow.
 *
 * Cognito (auth.lesmillsplus.com) redirects here after login, so the browser
 * address bar stays on a Les Mills domain instead of the raw execute-api host.
 * Outcomes the page can resolve on its own are handled client-side (denied,
 * missing params); a valid code+state is POSTed to device-auth
 * /auth/device/complete, which performs the privileged AUTHORIZED write and
 * returns a { status } the page renders. device-auth /auth/device/allow also
 * redirects here with `?outcome=EXPIRED` when the user code has expired, so
 * the user never lands on a raw JSON error. Look & feel mirrors the previous
 * server-rendered device-auth HTML template.
 */

type Outcome =
  | 'LOADING'
  | 'AUTHORIZED'
  | 'DENIED'
  | 'INVALID_REQUEST'
  | 'MULTIPLE_DEVICES'
  | 'DEVICE_NOT_FOUND'
  | 'EXPIRED'
  | 'TOKEN_NOT_FOUND'
  | 'INTERNAL';

interface OutcomeView {
  title: string;
  message: string;
  isSuccess: boolean;
}

// Copy migrated verbatim from the device-auth authDeviceCallback HTML responses.
const OUTCOMES: Record<Exclude<Outcome, 'LOADING'>, OutcomeView> = {
  AUTHORIZED: {
    title: 'Device Authorized Successfully!',
    message:
      'Your device has been successfully authorized. You can now return to your device and continue using the Les Mills app.',
    isSuccess: true,
  },
  DENIED: {
    title: 'Authorization Denied',
    message:
      'The device authorization was denied or cancelled. Please try again if this was a mistake.',
    isSuccess: false,
  },
  INVALID_REQUEST: {
    title: 'Invalid Request',
    message:
      'The authorization request is missing required parameters. Please try the device authorization process again.',
    isSuccess: false,
  },
  MULTIPLE_DEVICES: {
    title: 'Authorization Error',
    message:
      'Multiple devices found for this authorization request. Please restart the device authorization process.',
    isSuccess: false,
  },
  DEVICE_NOT_FOUND: {
    title: 'Device Not Found',
    message:
      'The device authorization request could not be found or has expired. Please restart the authorization process on your device.',
    isSuccess: false,
  },
  EXPIRED: {
    title: 'Code Expired',
    message: 'This sign-in code has expired. Scan the new QR code on your device to try again.',
    isSuccess: false,
  },
  TOKEN_NOT_FOUND: {
    title: 'Authorization Error',
    message:
      'The device token could not be found. Please restart the authorization process on your device.',
    isSuccess: false,
  },
  INTERNAL: {
    title: 'Something Went Wrong',
    message:
      'We encountered an unexpected error while processing your device authorization. Please try again or contact support if the problem persists.',
    isSuccess: false,
  },
};

const FONT_STACK = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

const Page = styled.div`
  font-family: ${FONT_STACK};
  background: #f8fafc;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #1e293b;
`;

const Container = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 60px 48px;
  text-align: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  max-width: 480px;
  width: 100%;
  border: 1px solid #e2e8f0;

  @media (max-width: 480px) {
    padding: 40px 32px;
    margin: 16px;
    border-radius: 12px;
  }
`;

const StatusBadge = styled.div<{ variant: 'success' | 'error' | 'loading' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin: 0 auto 32px;
  font-size: 36px;
  font-weight: 600;
  ${({ variant }) => {
    if (variant === 'success')
      return 'background: #dcfce7; color: #16a34a; border: 2px solid #bbf7d0;';
    if (variant === 'error')
      return 'background: #fef2f2; color: #dc2626; border: 2px solid #fecaca;';
    return 'background: #f1f5f9; color: #64748b; border: 2px solid #e2e8f0;';
  }}

  @media (max-width: 480px) {
    width: 64px;
    height: 64px;
    font-size: 28px;
    margin-bottom: 24px;
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.span`
  width: 32px;
  height: 32px;
  border: 3px solid #cbd5e1;
  border-top-color: #64748b;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 2.4s;
  }
`;

const Title = styled.h1`
  color: #0f172a;
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 16px;
  letter-spacing: -0.02em;
  line-height: 1.1;

  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  color: #64748b;
  font-size: 18px;
  line-height: 1.5;
  margin-bottom: 40px;
  font-weight: 400;

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, #e2e8f0 50%, transparent);
  margin: 40px 0;
`;

const Logo = styled.div`
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 12px;
  letter-spacing: -0.02em;

  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const Instruction = styled.p`
  font-size: 15px;
  color: #64748b;
  font-weight: 400;
  line-height: 1.4;
`;

const resolveOutcome = async (search: string): Promise<Outcome> => {
  const params = new URLSearchParams(search);
  const error = params.get('error');
  const code = params.get('code');
  const state = params.get('state');

  // States the page can resolve without a backend call. `outcome` is
  // allow-listed rather than looked up in OUTCOMES so a crafted link cannot
  // render the success view.
  if (params.get('outcome') === 'EXPIRED') return 'EXPIRED';
  if (error) return 'DENIED';
  if (!code || !state) return 'INVALID_REQUEST';

  try {
    const response = await fetch(`${process.env.GATSBY_DEVICE_AUTH_API_URL}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, state }),
    });

    const data = (await response.json().catch(() => null)) as {
      status?: string;
      code?: string;
    } | null;

    if (!data?.status) return 'INTERNAL';
    if (data.status === 'AUTHORIZED') return 'AUTHORIZED';
    if (data.status === 'INVALID_REQUEST') return 'INVALID_REQUEST';
    if (data.status === 'ERROR' && data.code && data.code in OUTCOMES) {
      return data.code as Outcome;
    }
    return 'INTERNAL';
  } catch {
    return 'INTERNAL';
  }
};

const DeviceAuthorized: React.FC = () => {
  const location = useLocation();
  const [outcome, setOutcome] = useState<Outcome>('LOADING');

  useEffect(() => {
    let active = true;
    resolveOutcome(location.search)
      .then((result) => {
        if (active) setOutcome(result);
      })
      .catch(() => {
        if (active) setOutcome('INTERNAL');
      });
    return () => {
      active = false;
    };
  }, [location.search]);

  if (outcome === 'LOADING') {
    return (
      <Page>
        <Container role="status" aria-live="polite" aria-atomic="true">
          <StatusBadge variant="loading" aria-hidden>
            <Spinner />
          </StatusBadge>
          <Title>Authorizing your device…</Title>
          <Subtitle>Please wait while we finish authorizing your device.</Subtitle>
        </Container>
      </Page>
    );
  }

  const view = OUTCOMES[outcome];

  return (
    <Page>
      <Container role="status" aria-live="polite" aria-atomic="true">
        <StatusBadge variant={view.isSuccess ? 'success' : 'error'} aria-hidden>
          {view.isSuccess ? '✓' : '✗'}
        </StatusBadge>
        <Title>{view.title}</Title>
        <Subtitle>{view.message}</Subtitle>

        <Divider />

        <div>
          <Logo>LES MILLS+</Logo>
          <Instruction>You can now close this window and return to your device.</Instruction>
        </div>
      </Container>
    </Page>
  );
};

export default DeviceAuthorized;

export const Head = () => <title>Device Authorization | LES MILLS+</title>;
